import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
  { name: "Contact", path: "/contact" },
  {
    name: "Specialties",
    path: "/services",
    noLink: true,
    children: [
      { name: "Virtual Ketamine-Assisted Therapy", path: "/services" },
      { name: "Therapy for Hyper-Independence", path: "/specialties/therapy-for-hyper-independence" },
      { name: "Boundaries Therapist for Adult Children of Emotionally Immature Parents", path: "/specialties/boundaries-therapist-emotionally-immature-parents" },
      { name: "Disorganized Attachment & KAP", path: "/specialties/disorganized-attachment-kap" },
    ],
  },
  {
    name: "Locations",
    path: "/services",
    noLink: true,
    children: [
      {
        name: "Ketamine-Assisted Therapy for Hyper-Independent Women in Brickell Miami, FL",
        path: "/specialties/online-ketamine-therapy-women-miami",
      },
      {
        name: "Ketamine-Assisted Therapy for Hyper-Independent Women in Cumberland, ME",
        path: "/specialties/ketamine-therapy-hyper-independent-women-cumberland-maine",
      },
      {
        name: "Ketamine-Assisted Therapy for Hyper-Independent Women in Portland, ME",
        path: "/specialties/ketamine-therapy-hyper-independent-women-portland-maine",
      },
      {
        name: "Ketamine-Assisted Therapy for Hyper-Independent Women in Washington, DC",
        path: "/specialties/ketamine-therapy-hyper-independent-women-washington-dc",
      },
    ],
  },
  {
    name: "Offerings",
    path: "/fees",
    noLink: true,
    children: [
      { name: "Virtual Ketamine-Assisted Therapy Intensives for Hyper-Independent Women in FL, ME, & DC", path: "/fees" },
      {
        name: "9-Week Group Intensive for Hyper-Independent Women in FL & ME",
        path: "/specialties/ketamine-therapy-group-immersion-fl-me",
      },
    ],
  },
  { name: "My Book", path: "/book" },
  { name: "Blog", path: "/blog" },
  { name: "FAQ", path: "/faq" },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState({});
  const location = useLocation();
  const dropdownTimerRef = useRef(null);

  const isActive = (path) => location.pathname === path;
  const isParentActive = (link) => {
    if (isActive(link.path)) return true;
    return link.children?.some((c) => isActive(c.path));
  };

  // Close dropdown on route change
  useEffect(() => {
    setOpenDropdown(null);
    setIsOpen(false);
  }, [location.pathname]);

  const handleDropdownEnter = (name) => {
    if (dropdownTimerRef.current) clearTimeout(dropdownTimerRef.current);
    setOpenDropdown(name);
  };
  const handleDropdownLeave = () => {
    dropdownTimerRef.current = setTimeout(() => setOpenDropdown(null), 150);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-navbar" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 text-primary"
            data-testid="nav-logo"
          >
            <img
              src="https://customer-assets-rejwkqb3.emergentagent.net/job_833ff4cd-a791-443f-9fd4-023fdfa8f8c0/artifacts/aw1u5wyo_D.png"
              alt="Divine Love Wellness Enterprises Logo"
              className="h-16 w-auto object-contain"
              data-testid="nav-logo-image"
            />
            <span className="font-serif text-xl font-semibold tracking-tight">
              Divine Love Wellness Enterprises
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => {
              const hasChildren = !!link.children?.length;
              const testId = `nav-link-${link.name.toLowerCase()}`;
              const baseCls = `font-medium transition-colors hover:text-primary ${
                isParentActive(link) ? "text-primary" : "text-muted-foreground"
              }`;

              if (!hasChildren) {
                return (
                  <Link
                    key={link.name}
                    to={link.path}
                    data-testid={testId}
                    className={baseCls}
                  >
                    {link.name}
                  </Link>
                );
              }

              return (
                <div
                  key={link.name}
                  className="relative"
                  onMouseEnter={() => handleDropdownEnter(link.name)}
                  onMouseLeave={handleDropdownLeave}
                >
                  {link.noLink ? (
                    <button
                      type="button"
                      data-testid={testId}
                      aria-haspopup="true"
                      aria-expanded={openDropdown === link.name}
                      className={`${baseCls} inline-flex items-center gap-1 cursor-default`}
                      onClick={() => handleDropdownEnter(link.name)}
                    >
                      {link.name}
                      <ChevronDown
                        className={`h-4 w-4 transition-transform ${
                          openDropdown === link.name ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  ) : (
                  <Link
                    to={link.path}
                    data-testid={testId}
                    className={`${baseCls} inline-flex items-center gap-1`}
                    onClick={() => setOpenDropdown(null)}
                  >
                    {link.name}
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${
                        openDropdown === link.name ? "rotate-180" : ""
                      }`}
                    />
                  </Link>
                  )}

                  <AnimatePresence>
                    {openDropdown === link.name && (
                      <motion.div
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 6 }}
                        transition={{ duration: 0.18 }}
                        className="absolute left-0 top-full pt-3 z-50"
                        data-testid={`nav-dropdown-${link.name.toLowerCase()}`}
                      >
                        <div className="min-w-[300px] rounded-2xl bg-white shadow-hover border border-border/40 p-2">
                          {link.children.map((child) => (
                            <Link
                              key={child.path}
                              to={child.path}
                              data-testid={`nav-dropdown-item-${child.name.toLowerCase()}`}
                              className={`block px-4 py-3 rounded-xl text-sm transition-colors hover:bg-secondary/60 ${
                                isActive(child.path)
                                  ? "text-primary font-medium bg-secondary/40"
                                  : "text-foreground"
                              }`}
                              onClick={() => setOpenDropdown(null)}
                            >
                              {child.name}
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
            <a
              href="https://calendly.com/divinelovewellnessenterprises/consultationmeeting"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                className="rounded-full px-6 btn-scale bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="nav-cta-button"
              >
                Book Consultation
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-primary"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-button"
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden overflow-hidden"
              data-testid="mobile-menu"
            >
              <div className="py-4 space-y-3">
                {navLinks.map((link) => {
                  const hasChildren = !!link.children?.length;
                  const baseCls = `block font-medium transition-colors hover:text-primary ${
                    isParentActive(link) ? "text-primary" : "text-muted-foreground"
                  }`;

                  if (!hasChildren) {
                    return (
                      <Link
                        key={link.name}
                        to={link.path}
                        data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                        className={baseCls}
                        onClick={() => setIsOpen(false)}
                      >
                        {link.name}
                      </Link>
                    );
                  }

                  const expanded = !!mobileExpanded[link.name];
                  return (
                    <div key={link.name}>
                      <div className="flex items-start gap-2">
                        {link.noLink ? (
                          <button
                            type="button"
                            data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                            className={`${baseCls} flex-1 text-left`}
                            onClick={() =>
                              setMobileExpanded((prev) => ({
                                ...prev,
                                [link.name]: !prev[link.name],
                              }))
                            }
                          >
                            {link.name}
                          </button>
                        ) : (
                        <Link
                          to={link.path}
                          data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                          className={`${baseCls} flex-1`}
                          onClick={() => setIsOpen(false)}
                        >
                          {link.name}
                        </Link>
                        )}
                        <button
                          type="button"
                          aria-label={`Toggle ${link.name} submenu`}
                          onClick={() =>
                            setMobileExpanded((prev) => ({
                              ...prev,
                              [link.name]: !prev[link.name],
                            }))
                          }
                          className="p-1 text-muted-foreground"
                          data-testid={`mobile-nav-toggle-${link.name.toLowerCase()}`}
                        >
                          <ChevronDown
                            className={`h-4 w-4 transition-transform ${
                              expanded ? "rotate-180" : ""
                            }`}
                          />
                        </button>
                      </div>
                      {expanded && (
                        <div className="mt-2 ml-3 pl-3 border-l border-border/40 space-y-2">
                          {link.children.map((child) => (
                            <Link
                              key={child.path}
                              to={child.path}
                              data-testid={`mobile-nav-dropdown-item-${child.name.toLowerCase()}`}
                              className={`block text-sm transition-colors hover:text-primary ${
                                isActive(child.path)
                                  ? "text-primary font-medium"
                                  : "text-muted-foreground"
                              }`}
                              onClick={() => setIsOpen(false)}
                            >
                              {child.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                <a
                  href="https://calendly.com/divinelovewellnessenterprises/consultationmeeting"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="mobile-cta-button"
                  >
                    Book Consultation
                  </Button>
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

export default Navbar;
